import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";

import { cn } from "@utils/ui";

const badgeVariants = cva(
  // Montserrat semibold, not bold — badges are labels (design: Typefaces).
  "inline-flex items-center justify-center rounded-full border px-3 py-1 text-xs font-head font-semibold w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 [a&]:hover:bg-slate-200 dark:[a&]:hover:bg-slate-700",
        primary:
          "border-transparent bg-primary-50 text-primary [a&]:hover:bg-primary/100",
        secondary:
          "border-transparent bg-secondary-50 text-secondary [a&]:hover:bg-secondary/100",
        // Text uses a darker shade than the bare color, not just for style —
        // green and amber/yellow both read as much "brighter" than their HSL
        // lightness suggests, so the
        // bare DEFAULT text against a -50 background measured well under
        // WCAG AA (success: 2.3:1, warning: 1.7:1). The -700/-800 shades
        // clear 4.5:1 comfortably and match the convention every other
        // consumer of these tints already uses (banner.tsx, alert.tsx, etc).
        success:
          "border-transparent bg-success-50 text-success-800 [a&]:hover:bg-success/100 [a&]:hover:text-success-foreground",
        warning:
          "border-transparent bg-warning-50 text-warning-800 [a&]:hover:bg-warning/100 [a&]:hover:text-warning-foreground",
        destructive:
          "border-transparent bg-destructive-50 text-destructive-700 [a&]:hover:bg-destructive/90 [a&]:hover:text-destructive-foreground focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        // Deprecated `accent` (purple) variant removed — zero consumers left
        // (migrated to `secondary`). Purple isn't part of the theme; don't
        // re-add it.
        outline:
          "text-foreground [a&]:hover:bg-secondary [a&]:hover:text-secondary-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
